import React, { useState } from 'react';
import PatientControlButton from 'components/atoms/patentControlButton';
import ControlButtonDropdown from 'components/atoms/controlPanelDropdownButton/controlButtonDropdown';
import { useTranslation } from 'react-i18next';
import CallControlIcon from 'components/atoms/icons/callControlIcon';
import { useCallButtons } from 'infrastructure/hooks/control-panel';
import ManualEnterCall from 'components/organisms/controlPanel/manualEnteredCallModal';
import useUser from 'utils/useUser';
import BaseGrid from 'components/atoms/base-grid';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { useGetDoNotCallInfo } from 'infrastructure/hooks/control-panel/use-get-call-info';

import type { ModuleType } from 'infrastructure/enums';

const ToggleButton: React.FC<{ label: string; disabled?: boolean }> = (
  props,
) => {
  const { label, disabled } = props;
  return (
    <PatientControlButton
      label={label}
      Icon={CallControlIcon}
      disabled={disabled}
    />
  );
};

interface IProps {
  module: ModuleType;
  patientGuid: string;
}

const CallButtonsController: React.FC<IProps> = (props) => {
  const { module, patientGuid } = props;
  const { t } = useTranslation();

  const [isRpmManualOpen, setIsRpmManualOpen] = useState(false);
  const [isCcmManualOpen, setIsCcmManualOpen] = useState(false);

  const { userType, roleNames, timezone, isMobileDevice } = useUser();
  const {
    patientInfo,
    doctorInfo,
    isRpmModule,
    isCcmModule,
    isCombinedModule,
    isBlockCcmModule,
    isBlockRpmModule,
    isCcmActionDisabled,
  } = useAccess({
    module,
    patientGuid,
  });

  const {
    doNotCallRpm,
    rpmDoNotCallReasonMessage,
    doNotCallCcm,
    ccmDoNotCallReasonMessage,
  } = useGetDoNotCallInfo();

  const { callButtons: rpmCallButtons, callButtonsAlert: rpmCallButtonsAlert } =
    useCallButtons({
      patientGuid,
      userType,
      doNotCall: doNotCallRpm,
      doNotCallReason: rpmDoNotCallReasonMessage,
      emergencyContactNumber: patientInfo?.emergencyContactNumber ?? '',
      patientHomeNumber: patientInfo?.homeNumber ?? '',
      patientPhone: patientInfo?.loginInfo?.phone ?? '',
      doctorPhone: doctorInfo?.phone,
      setRpmManualEnteredCallPopupOpen: setIsRpmManualOpen,
      roleNames,
      patientTimeZone: timezone,
      module,
    });

  const { callButtons: ccmCallButtons, callButtonsAlert: ccmCallButtonsAlert } =
    useCallButtons({
      patientGuid,
      userType,
      doNotCall: doNotCallCcm,
      doNotCallReason: ccmDoNotCallReasonMessage,
      emergencyContactNumber: patientInfo?.emergencyContactNumber ?? '',
      patientHomeNumber: patientInfo?.homeNumber ?? '',
      patientPhone: patientInfo?.loginInfo?.phone ?? '',
      doctorPhone: doctorInfo?.phone,
      setCcmManualEnteredCallPopupOpen: setIsCcmManualOpen,
      roleNames,
      patientTimeZone: timezone,
      module,
    });

  const getLabel = (prefix: string) => {
    const label = t('labels.call');
    if (isCombinedModule) return `${prefix} ${label}`;
    return label;
  };

  const columns = isMobileDevice || !isCombinedModule ? 1 : 2;

  return (
    <>
      <BaseGrid columns={columns}>
        {(isRpmModule || isCombinedModule) && (
          <ControlButtonDropdown
            toggleBtn={
              <ToggleButton
                label={getLabel(t('labels.rpm'))}
                disabled={isCcmActionDisabled || isBlockRpmModule}
              />
            }
            options={rpmCallButtons}
            isRpm
          />
        )}

        {(isCcmModule || isCombinedModule) && (
          <ControlButtonDropdown
            toggleBtn={
              <ToggleButton
                label={getLabel(t('labels.ccm'))}
                disabled={isCcmActionDisabled || isBlockCcmModule}
              />
            }
            options={ccmCallButtons}
            isRpm={false}
          />
        )}
      </BaseGrid>
      <ManualEnterCall
        isOpen={isRpmManualOpen}
        toggle={setIsRpmManualOpen}
        patientGuid={patientGuid}
        isRpmPanel
      />
      <ManualEnterCall
        isOpen={isCcmManualOpen}
        toggle={setIsCcmManualOpen}
        patientGuid={patientGuid}
        isRpmPanel={false}
      />
      {rpmCallButtonsAlert}
      {ccmCallButtonsAlert}
    </>
  );
};

export default CallButtonsController;
